import React, { useState, useEffect, useRef } from 'react';
import FurnitureNavbar from '../../../components/layout/Nav';
import './artisanprofile.scss';
import withAuth from '../../auth/WithAuth';
import defaultAvatar from '../../../assets/DefaultProfile.png';
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaLock,
  FaCamera,
  FaCheck,
  FaExclamationTriangle,
  FaEdit,
  FaTimes,
  FaImage,
  FaStar,
  FaAward,
  FaPlus,
} from 'react-icons/fa';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import Carousel from 'react-bootstrap/Carousel';
import { serverUrlAuth } from '../../../serverUrl';
import { toast } from 'react-toastify';

const ArtisanProfile = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    avatar: defaultAvatar,
    location: '',
    experience_years: 0,
    rating: 0,
    completed_projects: 0,
  });
  const [editable, setEditable] = useState(false);
  const [alerts, setAlerts] = useState({
    profile: '',
    legacy: '',
    portfolio: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  // Portfolio & legacy project state
  const [portfolioData, setPortfolioData] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  const [legacyForm, setLegacyForm] = useState({
    title: '',
    description: '',
    rating: 1,
    images: [],
  });
  const fileInputRef = useRef(null);

  // Fetch user profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await serverUrlAuth.get('users/profile/');
        const data = res.data;
        setProfile((prev) => ({
          ...prev,
          name: data.full_name,
          email: data.email,
          phone: data.phone_number || '',
          avatar: data.profile_picture || defaultAvatar,
        }));
      } catch (error) {
        console.error('Failed to fetch profile:', error);
      }
    };
    fetchProfile();
  }, []);

  // Fetch portfolio and stats
  const loadPortfolio = async () => {
    try {
      const res = await serverUrlAuth.get('portfolio/artisan-portfolio/');
      const data = res.data;
      setPortfolioData(res.data);
      setPortfolio(data.portfolio_items);
      setProfile((prev) => ({
        ...prev,
        experience_years: data.years_of_experience,
        rating: data.client_rating,
        completed_projects: data.projects_completed,
      }));
    } catch (error) {
      console.error('Failed to load portfolio:', error);
      showAlert('portfolio', 'Failed to load portfolio', 'error');
    }
  };

  useEffect(() => {
    loadPortfolio();
  }, []);

  const showAlert = (type, message, variant = 'success') => {
    setAlerts((prev) => ({ ...prev, [type]: { message, variant } }));
    setTimeout(() => setAlerts((prev) => ({ ...prev, [type]: '' })), 5000);
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/'))
        return showAlert('profile', 'Please select a valid image', 'error');
      if (file.size > 5 * 1024 * 1024)
        return showAlert('profile', 'Image must be less than 5MB', 'error');

      const imageUrl = URL.createObjectURL(file);
      setProfile((prev) => ({ ...prev, avatar: imageUrl }));
      showAlert('profile', 'Avatar updated locally', 'success');
    }
  };

  const toggleEdit = () => setEditable(true);
  const cancelEdit = () => setEditable(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('full_name', profile.name);
      formData.append('phone_number', profile.phone);
      formData.append('location', profile.location);
      if (fileInputRef.current?.files[0])
        formData.append('profile_picture', fileInputRef.current.files[0]);

      const res = await serverUrlAuth.patch('users/profile/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setProfile((prev) => ({
        ...prev,
        avatar: res.data.profile_picture || prev.avatar,
      }));
      showAlert('profile', 'Profile updated successfully!', 'success');
      setEditable(false);
    } catch (error) {
      console.error(error);
      showAlert('profile', 'Failed to update profile', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // --- LEGACY PROJECT HANDLERS ---
  const handleLegacyChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'images') {
      setLegacyForm((prev) => ({ ...prev, images: files }));
    } else {
      setLegacyForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const saveLegacyProject = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append('title', legacyForm.title);
      formData.append('description', legacyForm.description);
      formData.append('rating', legacyForm.rating);

      for (let i = 0; i < legacyForm.images.length; i++) {
        formData.append('images', legacyForm.images[i]);
      }

      await serverUrlAuth.post(
        'portfolio/artisan-portfolio/legacy/create/',
        formData,
      );

      toast.success( 'Legacy project added successfully to your portfolio!' );
      setLegacyForm({ title: '', description: '', rating: 1, images: [] });

      // Refresh portfolio
      loadPortfolio();
    } catch (err) {
      console.error(err);
      const msg =
        err.response?.data?.detail ||
        'Failed to add legacy project. Maybe you reached the 5-project limit.';
      showAlert('legacy', msg, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <FurnitureNavbar />
      <div className="profile-page">
        <div className="profile-layout">
          <div className="vertical-tabs">
            <button
              className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              <FaUser className="tab-icon" /> Profile Information
            </button>
            {portfolioData?.legacy_projects_count < 5 && (
              <button
                className={`tab-btn ${activeTab === 'legacy' ? 'active' : ''}`}
                onClick={() => setActiveTab('legacy')}
              >
                <FaPlus className="tab-icon" /> Add Legacy Portfolio
              </button>
            )}

            <button
              className={`tab-btn ${activeTab === 'portfolio' ? 'active' : ''}`}
              onClick={() => setActiveTab('portfolio')}
            >
              <FaImage className="tab-icon" /> My Portfolio
            </button>
          </div>

          <div className="profile-content">
            {/* Alerts */}
            {['profile', 'legacy', 'portfolio'].map(
              (type) =>
                alerts[type] && (
                  <Alert
                    key={type}
                    variant={alerts[type].variant}
                    className="alert-custom"
                  >
                    {alerts[type].variant === 'success' ? (
                      <FaCheck />
                    ) : (
                      <FaExclamationTriangle />
                    )}
                    {alerts[type].message}
                  </Alert>
                ),
            )}
            {/* PROFILE TAB */}
            {activeTab === 'profile' && (
              <div className="profile-card">
                <div className="card-header">
                  <h3>Profile Information</h3>
                  <p>Update your artisan profile and showcase your expertise</p>
                </div>
                <div className="card-content">
                  <div className="avatar-section">
                    <div className="avatar-preview">
                      <img src={profile.avatar} alt="Profile Avatar" />
                    </div>
                    {editable && (
                      <>
                        <Button
                          className="change-avatar"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <FaCamera /> Change Avatar
                        </Button>
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleAvatarChange}
                          accept="image/*"
                          style={{ display: 'none' }}
                        />
                      </>
                    )}
                  </div>
                  <div className="info-section">
                    <Form onSubmit={saveProfile}>
                      <Form.Group className="form-group-custom">
                        <Form.Label>
                          <FaUser /> Full Name
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={profile.name}
                          onChange={handleProfileChange}
                          disabled={!editable}
                          required
                        />
                      </Form.Group>
                      <Form.Group className="form-group-custom">
                        <Form.Label>
                          <FaEnvelope /> Email
                        </Form.Label>
                        <Form.Control
                          type="email"
                          value={profile.email}
                          disabled
                        />
                      </Form.Group>
                      <Form.Group className="form-group-custom">
                        <Form.Label>
                          <FaPhone /> Phone
                        </Form.Label>
                        <Form.Control
                          type="tel"
                          name="phone"
                          value={profile.phone}
                          onChange={handleProfileChange}
                          disabled={!editable}
                        />
                      </Form.Group>
                      {!editable ? (
                        <Button
                          className="edit-profile-btn"
                          size="sm"
                          onClick={toggleEdit}
                        >
                          <FaEdit /> Edit Profile
                        </Button>
                      ) : (
                        <div className="form-actions">
                          <Button
                            className="save-changes-btn"
                            type="submit"
                            disabled={isLoading}
                          >
                            {isLoading ? 'Saving...' : 'Save Changes'}
                          </Button>
                          <Button variant="secondary" onClick={cancelEdit}>
                            <FaTimes /> Cancel
                          </Button>
                        </div>
                      )}
                    </Form>
                  </div>
                </div>
              </div>
            )}
            {/* LEGACY PROJECT TAB */}
            {activeTab === 'legacy' && (
              <div className="profile-card legacy-form-container">
                <div className="card-header">
                  <h3>Add Legacy Project</h3>
                  <p className="mb-0">
                    Showcase your best work to attract more clients. You can add
                    up to 5 projects.
                  </p>
                </div>

                <div className="card-content-legacy">
                  <Form onSubmit={saveLegacyProject} className="legacy-form">
                    <div className="form-row">
                      <Form.Group className="form-group-custom">
                        <Form.Label className="form-label-with-icon">
                          <FaEdit className="label-icon" /> Project Title
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="title"
                          value={legacyForm.title}
                          onChange={handleLegacyChange}
                          placeholder="e.g., Custom Oak Dining Table"
                          className="form-control-lg"
                          required
                        />
                        <Form.Text className="text-muted">
                          Give your project a descriptive name
                        </Form.Text>
                      </Form.Group>

                      <Form.Group className="form-group-custom">
                        <Form.Label className="form-label-with-icon">
                          <FaStar className="label-icon" /> Your Rating
                        </Form.Label>
                        <div className="rating-selector">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              className={`rating-star-btn ${
                                legacyForm.rating >= star ? 'selected' : ''
                              }`}
                              onClick={() =>
                                setLegacyForm((prev) => ({
                                  ...prev,
                                  rating: star,
                                }))
                              }
                            >
                              <FaStar />
                            </button>
                          ))}
                          <span className="rating-value">
                            ({legacyForm.rating}/5)
                          </span>
                        </div>
                      </Form.Group>
                    </div>

                    <Form.Group className="form-group-custom">
                      <Form.Label className="form-label-with-icon">
                        <FaEdit className="label-icon" /> Project Description
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        name="description"
                        value={legacyForm.description}
                        onChange={handleLegacyChange}
                        rows={4}
                        placeholder="Describe the project details, materials used, challenges overcome, client requirements..."
                        className="form-control-lg"
                        required
                      />
                      <div className="description-counter">
                        <span>{legacyForm.description.length}</span>/500
                        characters
                      </div>
                    </Form.Group>

                    <Form.Group className="form-group-custom">
                      <Form.Label className="form-label-with-icon">
                        <FaCamera className="label-icon" /> Project Images
                      </Form.Label>
                      <div className="image-upload-container">
                        <div
                          className="image-upload-area"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          {legacyForm.images.length > 0 ? (
                            <div className="selected-images-preview">
                              <div className="images-count-badge">
                                {legacyForm.images.length} image(s) selected
                              </div>
                              <div className="image-preview-grid">
                                {Array.from(legacyForm.images)
                                  .slice(0, 3)
                                  .map((file, index) => (
                                    <div
                                      key={index}
                                      className="image-preview-item"
                                    >
                                      <img
                                        src={URL.createObjectURL(file)}
                                        alt={`Preview ${index + 1}`}
                                      />
                                      {index === 2 &&
                                        legacyForm.images.length > 3 && (
                                          <div className="more-images-overlay">
                                            +{legacyForm.images.length - 3}
                                          </div>
                                        )}
                                    </div>
                                  ))}
                              </div>
                            </div>
                          ) : (
                            <div className="upload-placeholder">
                              <div className="upload-icon">
                                <FaImage />
                              </div>
                              <h5>Click to upload images</h5>
                              <p>Drag & drop or click to browse</p>
                              <p className="upload-hint">
                                Supports JPG, PNG, WEBP up to 5MB each
                              </p>
                            </div>
                          )}
                          <input
                            type="file"
                            ref={fileInputRef}
                            name="images"
                            multiple
                            onChange={handleLegacyChange}
                            accept="image/*"
                            style={{ display: 'none' }}
                          />
                        </div>
                      </div>
                    </Form.Group>

                    <div className="form-actions legacy-form-actions">
                      <Button
                        type="submit"
                        className="submit-legacy-btn"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            />
                            Adding Project...
                          </>
                        ) : (
                          <>
                            <FaPlus className="me-2" />
                            Add to Portfolio
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </div>
              </div>
            )}
            {/* PORTFOLIO TAB */}
            {activeTab === 'portfolio' && (
              <div className="profile-card">
                <div className="card-header">
                  <h3>My Portfolio</h3>
                  <p>Showcase your craftsmanship and completed projects</p>
                </div>

                {/* Stats */}
                <div className="artisan-stats">
                  <div className="stat-card">
                    <div className="stat-icon">
                      <FaAward />
                    </div>
                    <div className="stat-content">
                      <h4>{profile.experience_years}</h4>
                      <p>Years Experience</p>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">
                      <FaImage />
                    </div>
                    <div className="stat-content">
                      <h4>{portfolio.length}</h4>
                      <p>Projects Showcased</p>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">
                      <FaStar />
                    </div>
                    <div className="stat-content">
                      <h4>{profile.rating}</h4>
                      <p>Client Rating</p>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">
                      <FaCheck />
                    </div>
                    <div className="stat-content">
                      <h4>{profile.completed_projects}</h4>
                      <p>Projects Completed</p>
                    </div>
                  </div>
                </div>

                {/* Portfolio */}
                {portfolio.length > 0 ? (
                  <div className="portfolio-container">
                    {portfolio.map((item) => (
                      <div className="portfolio-item" key={item.id}>
                        <div className="portfolio-gallery">
                          {item.images && item.images.length > 0 ? (
                            <Carousel
                              variant="dark"
                              indicators={item.images.length > 1}
                              controls={item.images.length > 1}
                              interval={null}
                            >
                              {item.images.map((image, index) => (
                                <Carousel.Item key={index}>
                                  <img
                                    className="d-block w-100"
                                    src={image.image}
                                    alt={`Project ${item.title} - ${index + 1}`}
                                  />
                                </Carousel.Item>
                              ))}
                            </Carousel>
                          ) : (
                            <div className="image-placeholder">
                              <FaImage />
                              <span>No images available</span>
                            </div>
                          )}
                        </div>
                        <div className="portfolio-content">
                          <div className="project-header">
                            <h5>{item.title}</h5>
                            {item.rating && (
                              <div className="project-rating">
                                <FaStar className="rating-star" />
                                <span>{item.rating}</span>
                              </div>
                            )}
                          </div>
                          <p className="project-description">
                            {item.description || 'No description provided.'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-portfolio">
                    <div className="empty-icon">
                      <FaImage />
                    </div>
                    <h5>No Projects Yet</h5>
                    <p>
                      Start building your portfolio by adding your completed
                      projects in the add legacy portfolio. Showcase your
                      craftsmanship and attract more clients.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default withAuth(ArtisanProfile, 'artisan');
